import { X } from "lucide-react";

import { TransferReasonSelect } from "@/components/mrp/TransferReasonSelect";
import { Input, Label } from "@/components/ui/Input";
import { ReadOnlyHoverLayer } from "@/components/ui/title-help";
import { cn, trimText } from "@/lib/utils";
import {
  purchaseOrderLocationLabel,
  purchaseOrderLocationPublicLocationId,
} from "@/lib/mrp";
import { TransferEndpointLocationSelect } from "@/components/mrp/TransferEndpointLocationSelect";
import { useTransferEndpointLocations } from "@/lib/transferEndpointLocations";
import type { MvPoLineTransferRequestForm } from "@/lib/purchaseOrderDraftForm";

export function PurchaseOrderDraftTransferRequestFields({
  transferRequest,
  transferRequestIndex,
  lineSku,
  locationCode,
  disabled,
  hubWritesAllowed,
  quantityHasError,
  onUpdate,
  onRemove,
}: {
  transferRequest: MvPoLineTransferRequestForm;
  transferRequestIndex: number;
  lineSku: string;
  locationCode: string;
  disabled: boolean;
  hubWritesAllowed: boolean;
  quantityHasError?: boolean;
  onUpdate: (patch: Partial<MvPoLineTransferRequestForm>) => void;
  onRemove: () => void;
}): JSX.Element {
  const { locations: transferLocations } = useTransferEndpointLocations();
  const originPublicLocationId = purchaseOrderLocationPublicLocationId(locationCode);
  return (
    <div className={cn("space-y-2", transferRequestIndex > 0 && "border-t border-slate-200 pt-3")}>
      <div className="grid gap-2 md:grid-cols-12 md:items-end">
        <div className="md:col-span-2">
          <Label>Transfer quantity</Label>
          <Input
            type="number"
            min={1}
            value={transferRequest.quantity}
            onChange={(event) => onUpdate({ quantity: event.target.value })}
            disabled={disabled || !hubWritesAllowed}
            className={cn(quantityHasError && "border-rose-500 bg-rose-50 focus:border-rose-600 focus:ring-rose-500")}
          />
        </div>
        <div className="md:col-span-2">
          <Label>Origin</Label>
          <Input
            readOnly
            value={purchaseOrderLocationLabel(locationCode)}
            className="cursor-default bg-slate-100 text-slate-700"
            aria-readonly="true"
          />
        </div>
        <div className="md:col-span-2">
          <Label>Destination</Label>
          <TransferEndpointLocationSelect
            locations={transferLocations}
            value={transferRequest.destination_location_id}
            disabled={disabled || !hubWritesAllowed}
            excludeLocationId={originPublicLocationId}
            className="h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm disabled:opacity-60"
            onChange={(next) => onUpdate({ destination_location_id: next })}
          />
        </div>
        <div className="md:col-span-2">
          <TransferReasonSelect
            label="Reason"
            value={transferRequest.reason}
            disabled={disabled || !hubWritesAllowed}
            onChange={(reason) => onUpdate({ reason })}
          />
        </div>
        <div className="md:col-span-2">
          <Label>Target date</Label>
          <Input
            type="date"
            value={transferRequest.target_date}
            onChange={(event) => onUpdate({ target_date: event.target.value })}
            disabled={disabled || !hubWritesAllowed}
          />
        </div>
        <div className="md:col-span-2">
          <Label>Notes</Label>
          <div className="flex items-end gap-1">
            <Input
              value={transferRequest.notes}
              onChange={(event) => onUpdate({ notes: event.target.value })}
              disabled={disabled || !hubWritesAllowed}
              placeholder="Optional"
              className="min-w-0 flex-1"
            />
            <ReadOnlyHoverLayer
              active={!hubWritesAllowed}
              hoverHint="Your role has read-only access to the MRP hub module."
              layout="inline"
            >
              <button
                type="button"
                disabled={disabled || !hubWritesAllowed}
                onClick={onRemove}
                className="mb-0.5 shrink-0 rounded p-1 text-rose-600 hover:bg-rose-50 hover:text-rose-700 disabled:cursor-not-allowed disabled:opacity-40"
                aria-label={`Remove transfer request for ${trimText(lineSku) || "this SKU"}`}
              >
                <X className="h-4 w-4" aria-hidden />
              </button>
            </ReadOnlyHoverLayer>
          </div>
        </div>
      </div>
    </div>
  );
}
